import { Box, Button, CircularProgress, Grid, IconButton, Paper, Popover, Tooltip, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import "../css/BookingCalendar.css";
import { fmtKey, getMonthMatrix, isPastDate } from "../../../constants/DateUtils";
import { useEffect, useMemo, useState } from "react";
import { BookingTimeRangeType, printDebugLog, StayType } from "../../../constants/Constants";

const DatePanel = ({
    viewDate,
    bookingCount,
    readOnlyPast = true,
    disabledDate,
    previousDateBooking = [],
    currentDateSlot,
    isClickedSameDate,
    goNext,
    goPrev,
    onClickFull, onClickPartial, onClickCheckBooking, onClickSameDate,
    isDay = false,
    disabledHour = false,
    loader = false,
}) => {

    const [dateAnchor, setDateAnchor] = useState(null);
    const [datePopoverDay, setDatePopoverDay] = useState(null);

    const TODAYDATE = fmtKey(new Date());
    const MONTHDAYS = useMemo(() => getMonthMatrix(viewDate), [viewDate]);
    const dateOpen = Boolean(dateAnchor);
    const [sameDate, setSameDate] = useState(false);


    useEffect(() => {
        printDebugLog("useEffect bookingCount", bookingCount);
    }, [bookingCount])

    const totalBookingCountOnSameDay = (dateStr) => (bookingCount ? (bookingCount[dateStr] || 0) : 0);

    const openDatePopover = (event, d) => {
        if (disabledHour) {
            onClickFull(d);
        } else {
            setSameDate(isClickedSameDate(d));
            setDateAnchor(event.currentTarget);
            setDatePopoverDay(d);
        }
    };

    const closeDatePopover = () => {
        setDateAnchor(null);
        setDatePopoverDay(null);
    }

    const getStayType = (bookingType) => {
        if (bookingType === -1) {
            return "";
        }
        return bookingType === StayType.DAILY ? "Daily" : "Hourly";
    }

    const getBookingDayHoursMessage = (dKey, count) => {
        let nCount = isDay ? currentDateSlot?.size || 0 : currentDateSlot.get(dKey) || 0;
        nCount += count;
        nCount = Math.min(nCount, 24);
        const suffix = isDay ? "day" : "hour";
        return `${nCount} ${suffix}${count === 1 ? "" : "s"
            } booked`;
    }

    // const handleAddHourlyButtonAction = (day, alreadyBookingAvailable, bookingDetails) => {
    //     if (bookingDetails) {
    //         //navigate to directly booking section
    //     } else {
    //         handleOnDateAddPartial(day, alreadyBookingAvailable);
    //     }
    // }


    function PopoverContent({ day }) {
        const dKey = fmtKey(day);
        const count = totalBookingCountOnSameDay(dKey);
        const bookingDetails = previousDateBooking?.[dKey] || null;

        let timeRangeRemaining = -1;
        const currentTimeRange = currentDateSlot?.get(dKey) ?? -1;
        if (count === 0) {
            timeRangeRemaining = currentTimeRange === -1 ? BookingTimeRangeType.FULL : currentTimeRange === BookingTimeRangeType.FULL ? -1 : currentTimeRange;
        } else if (count > 0 && count < 24) {
            timeRangeRemaining = BookingTimeRangeType.PARTIAL;
        } else if (count === 24) {
            timeRangeRemaining = -1;
        }
        printDebugLog("PopoverContent", currentDateSlot, dKey, count, bookingDetails, currentTimeRange, timeRangeRemaining);

        return (
            <div className="bk-popover-content">
                <div className="bk-popover-row">
                    <span className="bk-popover-label">Date</span>
                    <span className="bk-popover-value">{day.toDateString()}</span>
                </div>

                {count === 1 && (
                    <div className="bk-popover-row">
                        <span className="bk-popover-label">Booking Id</span>
                        <span className="bk-popover-value">{bookingDetails?.bookingId ?? "—"}</span>
                    </div>
                )}
                {
                    count === 1 &&
                    (
                        <div className="bk-popover-row">
                            <span className="bk-popover-label">Stay type</span>
                            <span className="bk-popover-value">{getStayType(bookingDetails?.stayType ?? -1)}</span>
                        </div>
                    )
                }
                {
                    count === 1 &&
                    (
                        <div className="bk-popover-row">
                            <span className="bk-popover-label">CheckIn</span>
                            <span className="bk-popover-value">{bookingDetails?.checkInDateStr ?? "—"}</span>
                        </div>
                    )
                }
                {
                    count === 1 &&
                    (
                        <div className="bk-popover-row">
                            <span className="bk-popover-label">CheckOut</span>
                            <span className="bk-popover-value">{bookingDetails?.checkoutDateStr ?? "—"}</span>
                        </div>
                    )
                }

                <div className="bk-popover-row">
                    <span className="bk-popover-label">Booked Hours</span>
                    <span className="bk-popover-value">{getBookingDayHoursMessage(dKey, count)}</span>
                </div>

                <div className="bk-popover-actions">
                    {
                        (readOnlyPast && !isPastDate(day) && timeRangeRemaining === BookingTimeRangeType.FULL) &&
                        <Button size="small" onPointerDown={() => {
                            closeDatePopover();
                            onClickFull(day);
                        }}>
                            Select full day
                        </Button>
                    }
                    {
                        (!disabledHour && timeRangeRemaining > -1) && <Button size="small" onPointerDown={() => {
                            closeDatePopover();
                            onClickPartial(day, count > 0);
                        }}>
                            Select hourly
                        </Button>
                    }
                    {
                        sameDate && <Button size="small" onPointerDown={() => {
                            closeDatePopover();
                            onClickSameDate(day);
                        }}>
                            Select Same Day
                        </Button>
                    }
                    {
                        (timeRangeRemaining === -1 || timeRangeRemaining === BookingTimeRangeType.PARTIAL) && <Button size="small" onPointerDown={() => {
                            closeDatePopover();
                            onClickCheckBooking(-1, day, count > 0);
                        }}>
                            Check Booking
                        </Button>
                    }
                    {/* {
                        (count === 0 && timeRangeRemaining === -1) && <Button size="small" onPointerDown={() => {
                            closeDatePopover();
                            onClickFull(day);
                        }}>
                            Change Booking
                        </Button>
                    } */}
                </div>
            </div>
        );
    }


    return (
        <>
            <Grid item xs={12} md={6} lg={6} sm={12}>
                <Box sx={{ position: "relative" }}>
                    {/* ✅ Your actual UI, blurred & non-interactive while loading */}
                    <Box
                        sx={{
                            filter: loader ? "blur(3px)" : "none",
                            pointerEvents: loader ? "none" : "auto",
                            userSelect: loader ? "none" : "auto",
                            transition: "filter 160ms ease",
                        }}
                    >
                        <Paper elevation={2} className="bk-panel">
                            <Box className="bk-cal-header">
                                <IconButton size="small" onClick={goPrev}>
                                    <ArrowBackIosNewIcon fontSize="inherit" />
                                </IconButton>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {viewDate.toLocaleString(undefined, {
                                        month: "long",
                                        year: "numeric",
                                    })}
                                </Typography>
                                <IconButton size="small" onClick={goNext}>
                                    <ArrowForwardIosIcon fontSize="inherit" />
                                </IconButton>
                            </Box>

                            <Box className="bk-weekdays">
                                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
                                    <div key={w} className="bk-weekcell">
                                        {w}
                                    </div>
                                ))}
                            </Box>

                            <Box
                                className="bk-grid"
                            >
                                {MONTHDAYS.map((d, i) => {
                                    const inMonth = d.getMonth() === viewDate.getMonth();
                                    const dKey = fmtKey(d);
                                    const count = totalBookingCountOnSameDay(dKey);
                                    const isTodayDate = fmtKey(d) === TODAYDATE;
                                    const disabled =
                                        (readOnlyPast && isPastDate(d)) ||
                                        (disabledDate && disabledDate(d));

                                    let timeRangeRemaining = BookingTimeRangeType.NOT_BOOKED; // 0 means partial , 1 means == full , 2 means not booked
                                    if (count > 0 && count < 24) {
                                        timeRangeRemaining = BookingTimeRangeType.PARTIAL; //Partial Hours are remaining
                                    } else if (count === 24) {
                                        timeRangeRemaining = BookingTimeRangeType.FULL; //no slot remaining
                                    }
                                    //Cuurent Validation will override the previousBooking
                                    if (currentDateSlot.has(dKey)) {
                                        timeRangeRemaining = currentDateSlot.get(dKey);
                                    }

                                    const title = `${d.toDateString()} • ${getBookingDayHoursMessage(dKey, count)}`;

                                    return (
                                        <Tooltip key={i} title={title} arrow enterDelay={150}>
                                            <button
                                                className={`bk-day ${inMonth ? "" : "bk-day--muted"}
                                         ${timeRangeRemaining === BookingTimeRangeType.PARTIAL
                                                        ? "bk-day--partial" : timeRangeRemaining === BookingTimeRangeType.FULL
                                                            ? "bk-day--full" : ""} ${disabled ? "bk-day--disabled" : ""
                                                    }`}
                                                onClick={(e) => {
                                                    openDatePopover(e, d);
                                                }}
                                            >
                                                <span className="bk-daynum">{d.getDate()}</span>
                                                {isTodayDate && <span className="bk-dot" />}
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Paper>
                    </Box>
                    <Box
                        aria-hidden={!loader}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: loader ? "flex" : "none",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                            // subtle glassy backdrop
                            background: "rgba(255,255,255,0.25)",
                            backdropFilter: "blur(2px)",
                        }}
                    >
                        <CircularProgress size={24} thickness={4} />
                    </Box>
                </Box>
            </Grid>

            <Popover
                open={dateOpen}
                anchorEl={dateAnchor}
                onClose={closeDatePopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                PaperProps={{ className: "bk-popover-paper" }}
            >
                {datePopoverDay && <PopoverContent day={datePopoverDay} />}
            </Popover>
        </>
    )
};

export { DatePanel }